import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";

const SHOWCASE = [
  {
    id: "body-butter",
    number: "01",
    label: "// SIGNATURE",
    title: "Body Butter",
    tagline: "Whipped. Rich. Absorbs in seconds.",
    price: "$35",
    size: "12 oz",
    description:
      "A triple-butter formula whipped cold, twice — for a mousse-lift that melts into your skin. No fillers, no water, just raw shea and hand-blended oils.",
    highlights: [
      "Raw unrefined shea butter base",
      "Whipped cold — twice",
      "Non-greasy, deep-absorbing finish"
    ],
    image:
      "https://customer-assets.emergentagent.com/job_neon-workshop-3/artifacts/r7hwkp82_ChatGPT_Image_Jul_10__2026__11_39_43_AM-removebg-preview.png",
    href: "/product/body-butter",
    align: "right"
  },
  {
    id: "body-oil",
    number: "02",
    label: "// GLOW",
    title: "Body Oil",
    tagline: "Pour it. Rub it. Glow on.",
    price: "$12",
    size: "2 oz",
    description:
      "Cold-pressed carriers — rosehip, castor, vitamin E — engineered to seal in moisture and lay down a soft sheen without residue.",
    highlights: [
      "Rosehip + castor + vitamin E",
      "Non-comedogenic",
      "Instant sheen, zero residue"
    ],
    image:
      "https://customer-assets.emergentagent.com/job_neon-workshop-3/artifacts/fzbhdhtm_ChatGPT_Image_Jul_13__2026__12_39_11_PM-removebg-preview.png",
    href: "/product/body-oil",
    align: "left"
  },
  {
    id: "spray-oil",
    number: "03",
    label: "// MIST",
    title: "Spray Oil",
    tagline: "Mist. Set. Radiate.",
    price: "$20",
    size: "4 oz",
    description:
      "A finely-atomized spray oil that lays a weightless veil of moisture over the butter. Fastest absorption in the line and a long scent throw.",
    highlights: [
      "Fine-mist atomizer",
      "Layer over Body Butter",
      "Long scent throw"
    ],
    image:
      "https://customer-assets.emergentagent.com/job_neon-workshop-3/artifacts/7rzr8vvt_Jul_14__2026__05_36_03_PM-removebg-preview.png",
    href: "/product/spray-oil",
    align: "right"
  }
];

const ProductShowcase = () => {
  const sectionRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const totalScroll = rect.height - vh;
      if (totalScroll <= 0) return;
      const scrolled = -rect.top;
      const raw = scrolled / totalScroll;
      const clamped = Math.max(0, Math.min(1, raw));
      setProgress(clamped);
      // Divide 0..1 into SHOWCASE.length equal segments
      const idx = Math.min(SHOWCASE.length - 1, Math.floor(clamped * SHOWCASE.length));
      setActiveIdx(idx);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative border-t border-off-white/10 bg-charcoal-2"
      style={{ height: `${SHOWCASE.length * 100}vh` }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center">
        {/* Ambient rose halo */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-[0.14] blur-3xl transition-all duration-700"
            style={{
              background: "radial-gradient(circle, #D89999 0%, transparent 60%)",
              left: SHOWCASE[activeIdx].align === "right" ? "auto" : "-15%",
              right: SHOWCASE[activeIdx].align === "right" ? "-15%" : "auto"
            }}
          />
        </div>

        {/* Section header (fixed at top of sticky area) */}
        <div className="absolute top-8 left-0 right-0 px-6 md:px-10 z-20">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// THE LINE-UP · SCROLL</p>
            <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/60">
              <span className="text-off-white">{String(activeIdx + 1).padStart(2, "0")}</span>
              <span className="mx-2">/</span>
              <span>{String(SHOWCASE.length).padStart(2, "0")}</span>
            </p>
          </div>
          {/* Progress bar */}
          <div className="max-w-[1600px] mx-auto mt-3 h-px bg-off-white/10 relative">
            <div
              className="absolute inset-y-0 left-0 bg-rose transition-[width] duration-200"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>

        {/* Content grid */}
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 w-full grid md:grid-cols-2 gap-10 items-center relative z-10">
          {/* Product image column */}
          <div
            className={`relative h-[70vh] flex items-center justify-center transition-[order] ${
              SHOWCASE[activeIdx].align === "right" ? "md:order-2" : "md:order-1"
            }`}
          >
            {SHOWCASE.map((p, i) => {
              const isActive = i === activeIdx;
              const isPast = i < activeIdx;
              return (
                <img
                  key={p.id}
                  src={p.image}
                  alt={p.title}
                  className="absolute max-h-[68vh] w-auto object-contain transition-all duration-[900ms]"
                  style={{
                    filter:
                      "drop-shadow(0 40px 80px rgba(216,153,153,0.35)) drop-shadow(0 20px 40px rgba(0,0,0,0.6))",
                    opacity: isActive ? 1 : 0,
                    transform: isActive
                      ? "translateX(0) scale(1)"
                      : isPast
                      ? "translateX(-40%) scale(0.85)"
                      : "translateX(40%) scale(0.85)",
                    transitionTimingFunction: "cubic-bezier(0.65, 0, 0.35, 1)"
                  }}
                />
              );
            })}
          </div>

          {/* Info column */}
          <div
            className={`relative min-h-[60vh] flex items-center transition-[order] ${
              SHOWCASE[activeIdx].align === "right" ? "md:order-1" : "md:order-2"
            }`}
          >
            {SHOWCASE.map((p, i) => {
              const isActive = i === activeIdx;
              return (
                <div
                  key={p.id}
                  className="absolute inset-0 flex flex-col justify-center transition-all duration-700"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? "translateY(0)" : "translateY(24px)",
                    pointerEvents: isActive ? "auto" : "none"
                  }}
                >
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="font-display text-6xl md:text-7xl text-off-white/15">{p.number}</span>
                    <span className="font-mono text-[10px] tracking-[0.3em] text-rose">{p.label}</span>
                  </div>
                  <h2 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.9] tracking-tighter text-off-white">
                    {p.title}
                  </h2>
                  <p className="font-serif-display italic text-xl md:text-2xl text-rose mt-3">{p.tagline}</p>

                  <p className="mt-6 max-w-xl text-off-white/70 text-[15px] md:text-base font-light leading-relaxed">
                    {p.description}
                  </p>

                  <ul className="mt-6 space-y-2">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-3 text-off-white/85 text-sm">
                        <span className="w-5 h-5 border border-rose/60 flex items-center justify-center shrink-0">
                          <Check size={12} className="text-rose" strokeWidth={2} />
                        </span>
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex items-center gap-4 flex-wrap">
                    <Link
                      to={p.href}
                      className="btn-blueprint inline-flex items-center gap-3 h-12 px-6 bg-off-white text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold hover:bg-rose transition-colors-only"
                    >
                      SHOP {p.title.toUpperCase()} — {p.price}
                      <ArrowUpRight size={14} />
                    </Link>
                    <span className="font-mono text-[10px] tracking-[0.3em] text-off-white/50">
                      {p.size} · CRAFTED WITH LOVE — NATURALLY
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom step dots */}
        <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-3 z-20">
          {SHOWCASE.map((p, i) => (
            <div key={p.id} className="flex items-center gap-2">
              <span
                className={`h-1.5 transition-all duration-500 ${
                  i === activeIdx ? "w-10 bg-rose" : "w-4 bg-off-white/20"
                }`}
              />
              <span
                className={`font-mono text-[10px] tracking-[0.25em] transition-colors duration-500 ${
                  i === activeIdx ? "text-off-white" : "text-off-white/40"
                }`}
              >
                {p.title.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
